/* eslint-disable react/jsx-props-no-spreading -- react-hook-form Controller `field` */
import { CircularProgress, Stack, Typography } from '@mui/material';
import {
    Autocomplete,
    FormStepComponentProps,
    SIMDocumentTypeAttachmentOption,
    SortedSelect,
    TextField,
    useCountries,
    useCountryAndStateOptions,
    useCurrency,
} from 'common';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { PostSignupFormSchema } from '../schemas';
import { SimAttachmentsDropzone } from '../SimAttachmentsDropzone';
import { ServiceAgreementConsent } from '../ServiceAgreementConsent';
import { PostSignupAgreement, isPlaidAgreementNeeded } from '../../usePostSignupAgreements';
import { LockedFormNotice, LockedFormStatus } from '../LockedFormNotice';
import { PREFERRED_PAYMENT_METHODS } from './RemittanceFormViewModel';
import { PaymentFieldConfiguration } from './Remittance.utils';

type Props = FormStepComponentProps<typeof PostSignupFormSchema> & {
    attachmentOption: SIMDocumentTypeAttachmentOption;
    files: File[];
    onFilesChange: (files: File[]) => void;
    hasError: boolean;
    paymentFieldConfiguration: PaymentFieldConfiguration | null;
    taxDetailsScheduled: boolean;
    agreement: PostSignupAgreement | undefined;
    agreementAccepted: boolean;
    onAgreementAcceptedChange: (accepted: boolean) => void;
    lockedStatus: LockedFormStatus | null;
};

const PREFERRED_PAYMENT_METHOD_OPTIONS = [...PREFERRED_PAYMENT_METHODS];

export function RemittanceStep({
    control,
    attachmentOption,
    files,
    onFilesChange,
    hasError,
    paymentFieldConfiguration,
    taxDetailsScheduled,
    agreement,
    agreementAccepted,
    onAgreementAcceptedChange,
    lockedStatus,
}: Props) {
    const { data: currencyList = [], isLoading: isLoadingCurrencies } = useCurrency();
    const { data: countryList = [], isLoading: isLoadingCountries } = useCountries();

    const selectedRemittanceCountry = useWatch({ name: 'remittance.RemittanceCountry', control, exact: true }) ?? null;
    const selectedBankCountry = useWatch({ name: 'remittance.BankCountry', control, exact: true }) ?? null;

    const selectedPaymentMethod = useWatch({ name: 'remittance.PreferredPaymentMethod', control, exact: true }) ?? null;
    const shownAgreement = isPlaidAgreementNeeded(selectedPaymentMethod) ? agreement : undefined;

    useEffect(() => {
        if (shownAgreement == null && agreementAccepted) {
            onAgreementAcceptedChange(false);
        }
    }, [shownAgreement, agreementAccepted, onAgreementAcceptedChange]);
    const selectedPaymentTerm = useWatch({ name: 'remittance.PaymentTerms', control, exact: true }) ?? null;
    const remittanceTin = useWatch({ name: 'remittance.TIN', control, exact: true }) ?? '';
    const businessName = useWatch({ name: 'taxDetails.BusinessName', control, exact: true }) ?? '';
    const tin = useWatch({ name: 'taxDetails.TIN', control, exact: true }) ?? '';

    const { setValue, clearErrors } = useFormContext();

    const { countryOptions, stateOptions: remittanceStateOptions } = useCountryAndStateOptions({
        selectedCountryCode: selectedRemittanceCountry,
        countryList,
    });
    const { countryOptions: bankCountryOptions, stateOptions: bankStateOptions } = useCountryAndStateOptions({
        selectedCountryCode: selectedBankCountry,
        countryList,
    });

    const remittanceCountryHasStates = Object.keys(remittanceStateOptions).length > 0;
    const bankCountryHasStates = Object.keys(bankStateOptions).length > 0;

    const handleRemittanceCountrySelect = (value: string | null) => {
        clearErrors('remittance.RemittanceCountry');
        clearErrors('remittance.RemittanceState');
        setValue('remittance.RemittanceCountry', value ?? '', { shouldDirty: true });
        setValue('remittance.RemittanceState', '', { shouldDirty: true });
    };

    const handleBankCountrySelect = (value: string | null) => {
        clearErrors('remittance.BankCountry');
        clearErrors('remittance.BankState');
        setValue('remittance.BankCountry', value ?? '', { shouldDirty: true });
        setValue('remittance.BankState', '', { shouldDirty: true });
    };

    // Option map for SortedSelect: label -> currencyType_US (e.g. "USD").
    // Storing the ISO-like string makes the serialized XML self-describing
    // and avoids coupling the signup form to backend currency ids.
    const currencyOptions = useMemo(() => {
        return currencyList.reduce<Record<string, string>>((acc, c) => {
            const label = `${c.displayName} (${c.currencyType_US})`;
            acc[label] = c.currencyType_US;
            return acc;
        }, {});
    }, [currencyList]);

    const paymentMethodOptions = useMemo(
        () =>
            paymentFieldConfiguration?.PaymentMethods.Options.map((method) => ({
                label: method.Label,
                value: method.Value,
            })) ?? [],
        [paymentFieldConfiguration]
    );

    const defaultPaymentMethod = useMemo(
        () => paymentFieldConfiguration?.PaymentMethods.DefaultValue ?? null,
        [paymentFieldConfiguration]
    );

    const paymentTermOptions = useMemo(
        () =>
            paymentFieldConfiguration?.PaymentTerms.Options.map((term) => ({
                label: term.Label,
                value: term.Value,
            })) ?? [],
        [paymentFieldConfiguration]
    );

    const defaultPaymentTerm = useMemo(
        () => paymentFieldConfiguration?.PaymentTerms.DefaultValue ?? null,
        [paymentFieldConfiguration]
    );

    useEffect(function prefillPaymentMethodAndTerm() {
        if (!paymentFieldConfiguration) {
            if (!selectedPaymentMethod) {
                setValue('remittance.PreferredPaymentMethod', 'ACH', { shouldDirty: true });
            }
            return;
        }

        if (defaultPaymentMethod && !selectedPaymentMethod) {
            setValue('remittance.PreferredPaymentMethod', defaultPaymentMethod, { shouldDirty: true });
        }

        if (defaultPaymentTerm && !selectedPaymentTerm) {
            setValue('remittance.PaymentTerms', defaultPaymentTerm, { shouldDirty: true });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }}>
            <Typography variant="subtitle1">Remittance Information</Typography>
            <Typography variant="body2" color="text.secondary">
                Your buyer has requested remittance details. Please fill in the fields below.
                {taxDetailsScheduled && ' Tax identity comes from Tax Details; VAT-only suppliers need no TIN.'}
            </Typography>
            <LockedFormNotice status={lockedStatus} />

            {taxDetailsScheduled ? (
                <>
                    <TextField label="Business Name" value={businessName} disabled testId="remittance-business-name" />
                    <TextField label="TIN (EIN/SSN)" value={tin} disabled testId="remittance-tin" />
                </>
            ) : (
                <>
                    <Controller
                        name="remittance.BusinessName"
                        control={control}
                        rules={{
                            deps: ['remittance.TIN'],
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="Business Name"
                                required={!!remittanceTin}
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="remittance-business-name"
                            />
                        )}
                    />
                    <Controller
                        name="remittance.TIN"
                        control={control}
                        rules={{
                            deps: ['remittance.BusinessName'],
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label="TIN (EIN/SSN)"
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                testId="remittance-tin"
                            />
                        )}
                    />
                </>
            )}

            <Controller
                name="remittance.Instructions"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Remittance Instructions"
                        multiline
                        rows={2}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-instructions"
                    />
                )}
            />

            <Typography variant="subtitle2">Remittance Address</Typography>
            {isLoadingCountries ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size="20px" />
                </Stack>
            ) : (
                <Controller
                    name="remittance.RemittanceCountry"
                    control={control}
                    render={({ field, fieldState }) => (
                        <Autocomplete
                            id="remittance-country"
                            label="Country"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            value={field.value || null}
                            options={countryOptions}
                            onChange={handleRemittanceCountrySelect}
                            testId="remittance-country"
                            disableSorting
                        />
                    )}
                />
            )}
            <Controller
                name="remittance.RemittanceAddress1"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Street Address"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-address1"
                    />
                )}
            />
            <Controller
                name="remittance.RemittanceCity"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="City"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-city"
                    />
                )}
            />
            <Controller
                name="remittance.RemittanceState"
                control={control}
                render={({ field, fieldState }) => {
                    if (!remittanceCountryHasStates) {
                        return (
                            <TextField
                                {...field}
                                label="State / Region"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                disabled={!selectedRemittanceCountry}
                                testId="remittance-state"
                            />
                        );
                    }
                    return (
                        <Autocomplete
                            id="remittance-state"
                            label="State / Region"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            value={field.value || null}
                            options={remittanceStateOptions}
                            onChange={(v) => field.onChange(v ?? '')}
                            disabled={!selectedRemittanceCountry}
                            testId="remittance-state"
                        />
                    );
                }}
            />
            <Controller
                name="remittance.RemittancePostalCode"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Postal Code"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-postal"
                    />
                )}
            />
            <Controller
                name="remittance.RemittancePhone"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Phone"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-phone"
                    />
                )}
            />
            <Controller
                name="remittance.RemittanceEmail"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Email"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-email"
                    />
                )}
            />

            <Typography variant="subtitle2">Bank Information</Typography>
            <Controller
                name="remittance.BankName"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Bank Name"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-bank-name"
                    />
                )}
            />
            {isLoadingCountries ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size="20px" />
                </Stack>
            ) : (
                <Controller
                    name="remittance.BankCountry"
                    control={control}
                    render={({ field, fieldState }) => (
                        <Autocomplete
                            id="remittance-bank-country"
                            label="Bank Country"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            value={field.value || null}
                            options={bankCountryOptions}
                            onChange={handleBankCountrySelect}
                            testId="remittance-bank-country"
                            disableSorting
                        />
                    )}
                />
            )}
            <Controller
                name="remittance.BankAddress1"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Bank Street Address"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-bank-address"
                    />
                )}
            />
            <Controller
                name="remittance.BankCity"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Bank City"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-bank-city"
                    />
                )}
            />
            <Controller
                name="remittance.BankState"
                control={control}
                render={({ field, fieldState }) => {
                    if (!bankCountryHasStates) {
                        return (
                            <TextField
                                {...field}
                                label="Bank State / Region"
                                required
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                disabled={!selectedBankCountry}
                                testId="remittance-bank-state"
                            />
                        );
                    }
                    return (
                        <Autocomplete
                            id="remittance-bank-state"
                            label="Bank State / Region"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            value={field.value || null}
                            options={bankStateOptions}
                            onChange={(v) => field.onChange(v ?? '')}
                            disabled={!selectedBankCountry}
                            testId="remittance-bank-state"
                        />
                    );
                }}
            />
            <Controller
                name="remittance.BankPostalCode"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Bank Postal Code"
                        required
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-bank-postal"
                    />
                )}
            />
            <Controller
                name="remittance.SWIFT"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="SWIFT"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-swift"
                    />
                )}
            />
            <Controller
                name="remittance.IBAN"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="IBAN"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-iban"
                    />
                )}
            />
            <Controller
                name="remittance.ABARoutingNumber"
                control={control}
                rules={{
                    deps: ['remittance.AccountNumber'],
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="ABA Routing Number"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-aba"
                    />
                )}
            />
            <Controller
                name="remittance.AccountNumber"
                control={control}
                rules={{
                    deps: ['remittance.ABARoutingNumber'],
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Account Number"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-account-number"
                    />
                )}
            />
            <Controller
                name="remittance.SortCode"
                control={control}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Sort Code"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="remittance-sort-code"
                    />
                )}
            />
            <Typography variant="subtitle2">Payment</Typography>
            {paymentMethodOptions.length > 0 ? (
                <Controller
                    name="remittance.PreferredPaymentMethod"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SortedSelect
                            id="remittance-preferred-payment-method"
                            label="Preferred Payment Method"
                            required
                            options={paymentMethodOptions}
                            value={paymentMethodOptions.find((option) => option.value === field.value) ?? null}
                            onChange={(v) => field.onChange(v?.value ?? '')}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            testId="remittance-preferred-payment-method"
                        />
                    )}
                />
            ) : (
                <Controller
                    name="remittance.PreferredPaymentMethod"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SortedSelect
                            id="remittance-preferred-payment-method"
                            label="Preferred Payment Method"
                            required
                            options={PREFERRED_PAYMENT_METHOD_OPTIONS}
                            value={field.value as typeof PREFERRED_PAYMENT_METHODS[number]}
                            onChange={(v) => field.onChange(v)}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            testId="remittance-preferred-payment-method"
                        />
                    )}
                />
            )}
            {paymentTermOptions.length > 0 ? (
                <Controller
                    name="remittance.PaymentTerms"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SortedSelect
                            label="Payment Terms"
                            required
                            options={paymentTermOptions}
                            value={paymentTermOptions.find((option) => option.value === field.value) ?? null}
                            onChange={(v) => field.onChange(v?.value ?? '')}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            testId="remittance-payment-terms"
                        />
                    )}
                />
            ) : (
                <Controller
                    name="remittance.PaymentTerms"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            label="Payment Terms"
                            required
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            testId="remittance-payment-terms"
                        />
                    )}
                />
            )}
            {isLoadingCurrencies ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size="20px" />
                </Stack>
            ) : (
                <Controller
                    name="remittance.DefaultPaymentCurrency"
                    control={control}
                    render={({ field, fieldState }) => (
                        <Autocomplete
                            id="remittance-default-payment-currency"
                            label="Default Payment Currency"
                            required
                            options={currencyOptions}
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            testId="remittance-default-payment-currency"
                            disableSorting
                        />
                    )}
                />
            )}
            <SimAttachmentsDropzone
                attachmentOption={attachmentOption}
                files={files}
                onFilesChange={onFilesChange}
                hasError={hasError}
                testId="remittance-attachments"
            />
            {shownAgreement != null && (
                <ServiceAgreementConsent
                    agreement={shownAgreement}
                    stepKey="remittance"
                    accepted={agreementAccepted}
                    onAcceptedChange={onAgreementAcceptedChange}
                />
            )}
        </Stack>
    );
}
